import { Box, Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { categoriesData } from "../../helpers/Arrays";
import { Gallery } from "../../helpers/gallery";
import { Images } from "../../helpers/Images";
import { ChevronDown } from "lucide-react";
import CompetitionCard from "../../Components/molecules/Cards/CompetitionCard/CompetitionCard";
import "./Categories.css";

const HIGHLIGHTS = [
  {
    title: "Age-Based Groups",
    description:
      "Three tracks tailored for Researchers, Engineers, and Pioneers.",
  },
  {
    title: "Diverse Competitions",
    description:
      "Robotics missions, line followers, scientific projects, and more.",
  },
  {
    title: "Skill Building",
    description:
      "Hands-on challenges that grow creativity, teamwork, and problem-solving.",
  },
];

const MAIN_CATEGORIES = [
  {
    id: "Researchers",
    title: "We Do Researchers",
    age: "4 – 6 years",
    image: Gallery.gallery19,
    accent: "#23aef4",
    tagline: "Playful STEAM exploration and early discovery",
  },
  {
    id: "Engineers",
    title: "We Do Engineers",
    age: "6 – 10 years",
    image: Gallery.gallery6,
    accent: "#c11a83",
    tagline: "Hands-on robotics and real-world problem solving",
  },
  {
    id: "Pioneers",
    title: "We Do Pioneers",
    age: "10 – 12 years",
    image: Gallery.gallery2,
    accent: "#23aef4",
    tagline: "Advanced builds, teamwork, and innovation",
  },
];

const CategoriesTemplate = () => {
  const navigate = useNavigate();
  const [type, setType] = useState("Engineers");

  const activeCategory = MAIN_CATEGORIES.find((c) => c.id === type);
  const filteredCompetitions = categoriesData?.filter((ty) =>
    ty.type.includes(type),
  );

  const scrollToCategories = () => {
    document
      .getElementById("categories-content")
      .scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box className="categoriesPage">
      <Box
        className="categoriesHero"
        sx={{
          backgroundImage: `radial-gradient(1100px 620px at 15% 15%, rgba(35, 174, 244, 0.38), transparent 58%),
            radial-gradient(900px 520px at 88% 25%, rgba(193, 26, 131, 0.32), transparent 55%),
            linear-gradient(180deg, rgba(10, 14, 24, 0.78) 0%, rgba(10, 14, 24, 0.62) 100%),
            url(${Images.wedo})`,
        }}
      >
        <Box
          className="categoriesHeroInner"
          sx={{
            padding: {
              xs: "56px 20px 40px",
              sm: "72px 60px 48px",
              lg: "88px 120px 56px",
            },
          }}
        >
          <Typography className="categoriesHeroEyebrow">We Do Challenge</Typography>
          <Typography className="categoriesHeroTitle">Our Categories</Typography>
          <Box className="categoriesHeroAccent" />
          <Typography className="categoriesHeroSubtitle">
            Choose an age group to explore competitions designed for young
            innovators at every level — from playful early STEAM to advanced
            robotics challenges.
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={scrollToCategories}
            className="categoriesHeroCta"
          >
            Browse Categories
          </Box>

          <Box className="categoriesHeroHighlights">
            {HIGHLIGHTS.map((item) => (
              <Box key={item.title} className="categoriesHeroHighlight">
                <Typography className="categoriesHeroHighlightTitle">
                  {item.title}
                </Typography>
                <Typography className="categoriesHeroHighlightText">
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        id="categories-content"
        sx={{
          width: "100%",
          maxWidth: "1500px",
          margin: "auto",
          padding: { xs: "40px 16px", sm: "50px 60px", lg: "60px 120px" },
        }}
      >
        <Box className="categoriesSectionHeader">
          <Typography className="categoriesSectionTitle">
            Select Your Age Group
          </Typography>
          <Box className="categoriesSectionAccent" />
          <Typography className="categoriesSectionSubtitle">
            Tap a category to see the competitions available for that age range.
          </Typography>
        </Box>

        <Box className="categorySelector">
          {MAIN_CATEGORIES.map((category) => {
            const isActive = type === category.id;
            return (
              <Box
                key={category.id}
                className={`categoryPanel${isActive ? " categoryPanel--active" : ""}`}
                onClick={() => setType(category.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setType(category.id);
                  }
                }}
                sx={{
                  "--category-accent": category.accent,
                }}
              >
                <Box
                  className="categoryPanelImage"
                  sx={{ backgroundImage: `url(${category.image})` }}
                />
                <Box className="categoryPanelOverlay" />
                <Box className="categoryPanelAccentBar" />
                <Box className="categoryPanelContent">
                  <Typography className="categoryPanelTitle">
                    {category.title}
                  </Typography>
                  <Typography className="categoryPanelAge">
                    {category.age}
                  </Typography>

                  {isActive ? (
                    <>
                      <Typography className="categoryPanelTagline">
                        {category.tagline}
                      </Typography>
                      <ChevronDown
                        size={22}
                        color="#fff"
                        className="categoryPanelChevron"
                      />
                    </>
                  ) : (
                    <Typography className="categoryPanelHint">
                      View competitions
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {type && (
          <Box className="competitionsSection">
            <Box className="competitionsSectionHeader">
              <Box
                className="competitionsSectionDot"
                sx={{ backgroundColor: activeCategory?.accent || "#c11a83" }}
              />
              <Typography className="competitionsSectionTitle">
                {activeCategory?.title} — Competitions
              </Typography>
              <Box
                className="competitionsSectionDot"
                sx={{ backgroundColor: activeCategory?.accent || "#c11a83" }}
              />
            </Box>

            <Grid container spacing={3} sx={{ justifyContent: "center" }}>
              {filteredCompetitions?.map((item, index) => (
                <Grid item xs={12} sm={6} lg={4} key={`${item.link}-${index}`}>
                  <CompetitionCard
                    item={item}
                    index={index}
                    categoryTitle={activeCategory?.title}
                    accent={activeCategory?.accent}
                    onClick={() => navigate(item.link)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CategoriesTemplate;
